import React, { useState, useEffect } from "react";
import {
  Trash2,
  Plus,
  Image as ImageIcon,
  DollarSign,
  Tag,
  Edit,
  Check,
  X,
} from "lucide-react";
import SizeSelector from "./SizeSelector";

export default function ProductVariantForm({
  variants,
  setVariants,
  chg,
  defaultPrice,
  defaultPromoPrice,
  isEditMode = false,
}) {
  const [currentVariant, setCurrentVariant] = useState({
    id: "",
    color: "#000000",
    colorName: "",
    sizes: [],
    imageFile: null,
    imagePreview: null,
    imageUrl: null,
    stock: 0,
    hasCustomPrice: false,
    price: defaultPrice || 0,
    isOnPromo: false,
    promoPrice: defaultPromoPrice || 0,
  });
  const [sizeType, setSizeType] = useState("clothing");
  const [editingVariantId, setEditingVariantId] = useState(null);
  const [deletedImageUrls, setDeletedImageUrls] = useState([]);

  useEffect(() => {
    // Reset current variant when exiting edit mode
    if (!editingVariantId) {
      setCurrentVariant({
        id: "",
        color: "#000000",
        colorName: "",
        sizes: [],
        imageFile: null,
        imagePreview: null,
        imageUrl: null,
        stock: 0,
        hasCustomPrice: false,
        price: defaultPrice || 0,
        isOnPromo: false,
        promoPrice: defaultPromoPrice || 0,
      });
    }
  }, [editingVariantId, defaultPrice, defaultPromoPrice]);

  const handleAddVariant = () => {
    if (currentVariant.colorName && currentVariant.sizes.length > 0) {
      if (editingVariantId) {
        // We're updating an existing variant
        const updatedVariants = variants.map((variant) => {
          if (variant.id === editingVariantId) {
            // If image changed, add old image URL to deletedImageUrls array
            if (currentVariant.imageFile && variant.imageUrl) {
              setDeletedImageUrls((prev) => [...prev, variant.imageUrl]);
            }

            return {
              ...currentVariant,
              // Keep the original ID and _id if they exist
              id: variant.id,
              _id: variant._id || null,
              // Keep the original imageUrl if no new image was selected
              imageUrl: currentVariant.imageFile ? null : variant.imageUrl,
            };
          }
          return variant;
        });

        setVariants(updatedVariants);
        setEditingVariantId(null);
      } else {
        // We're adding a new variant
        const newVariant = {
          ...currentVariant,
          id: Date.now().toString(),
          colorCode: currentVariant.color,
        };
        setVariants([...variants, newVariant]);
      }

      // Reset current variant
      setCurrentVariant({
        id: "",
        color: "#000000",
        colorName: "",
        sizes: [],
        imageFile: null,
        imagePreview: null,
        imageUrl: null,
        stock: 0,
        hasCustomPrice: false,
        price: defaultPrice || 0,
        isOnPromo: false,
        promoPrice: defaultPromoPrice || 0,
      });
    }
  };

  const handleRemoveVariant = (id, _id) => {
    const variantToRemove = variants.find((v) => v.id === id);

    // If there's an image URL, add it to deletedImageUrls
    if (variantToRemove?.imageUrl) {
      setDeletedImageUrls((prev) => [...prev, variantToRemove.imageUrl]);
    }

    if (_id && isEditMode) {
      chg(_id);
    }

    setVariants(variants.filter((v) => v.id !== id));

    // If we were editing this variant, cancel editing
    if (editingVariantId === id) {
      setEditingVariantId(null);
    }
  };

  const handleEditVariant = (variant) => {
    // Set appropriate size type based on the sizes
    let detectedSizeType = "custom";
    if (
      variant.sizes.every((size) =>
        ["XS", "S", "M", "L", "XL", "XXL", "3XL"].includes(size)
      )
    ) {
      detectedSizeType = "clothing";
    } else if (
      variant.sizes.every(
        (size) => parseInt(size) >= 36 && parseInt(size) <= 46
      )
    ) {
      detectedSizeType = "shoes";
    } else if (
      variant.sizes.every(
        (size) => parseInt(size) >= 30 && parseInt(size) <= 50
      )
    ) {
      detectedSizeType = "numeric";
    } else if (variant.sizes.length === 1 && variant.sizes[0] === "Unique") {
      detectedSizeType = "oneSize";
    }
    setSizeType(detectedSizeType);

    setCurrentVariant({
      ...variant,
      color: variant.color || variant.colorCode || "#000000",
      imageFile: null,
      imagePreview: variant.imageUrl || null,
    });
    setEditingVariantId(variant.id);
  };

  const cancelEdit = () => {
    setEditingVariantId(null);
  };

  const handleSizeToggle = (size) => {
    setCurrentVariant((prev) => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter((s) => s !== size)
        : [...prev.sizes, size],
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCurrentVariant((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: URL.createObjectURL(file),
      }));
    }
  };

  const handleCustomPriceToggle = (e) => {
    const hasCustomPrice = e.target.checked;
    setCurrentVariant((prev) => ({
      ...prev,
      hasCustomPrice,
      price: hasCustomPrice ? prev.price : defaultPrice || 0,
    }));
  };

  const handlePromoToggle = (e) => {
    const isOnPromo = e.target.checked;
    setCurrentVariant((prev) => ({
      ...prev,
      isOnPromo,
      promoPrice: isOnPromo ? prev.promoPrice : 0,
    }));
  };

  // Function to get deleted image URLs (can be called from parent component)
  const getDeletedImageUrls = () => {
    return deletedImageUrls;
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-3 sm:p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6">
        {isEditMode
          ? "Modifier les Variantes du Produit"
          : "Ajouter des Variantes du Produit"}
      </h2>

      <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8 p-3 sm:p-6 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Couleur || Variante
            </label>
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <input
                type="color"
                value={currentVariant.color}
                onChange={(e) =>
                  setCurrentVariant((prev) => ({
                    ...prev,
                    color: e.target.value,
                  }))
                }
                className="h-10 w-full sm:w-20 rounded cursor-pointer"
              />
              <input
                type="text"
                placeholder="Nom de la couleur ou variante"
                value={currentVariant.colorName}
                onChange={(e) =>
                  setCurrentVariant((prev) => ({
                    ...prev,
                    colorName: e.target.value,
                  }))
                }
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
              Image de la variante
            </label>
            <div className="flex items-center space-x-2">
              <label className="flex-1 cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <div className="w-full p-2 border rounded-md text-center hover:bg-gray-100 text-sm sm:text-base truncate">
                  {currentVariant.imageFile
                    ? currentVariant.imageFile.name
                    : currentVariant.imageUrl
                    ? "Changer l'image"
                    : "Choisir une image"}
                </div>
              </label>
              {(currentVariant.imagePreview || currentVariant.imageUrl) && (
                <div className="h-10 w-10 rounded-md overflow-hidden flex-shrink-0">
                  <img
                    src={currentVariant.imagePreview || currentVariant.imageUrl}
                    alt="Aperçu"
                    className="h-full w-full object-cover"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Stock disponible
          </label>
          <input
            type="number"
            min="0"
            value={currentVariant.stock}
            onChange={(e) =>
              setCurrentVariant((prev) => ({
                ...prev,
                stock: parseInt(e.target.value, 10) || 0,
              }))
            }
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Prix spécifique
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hasCustomPrice"
                  checked={currentVariant.hasCustomPrice}
                  onChange={handleCustomPriceToggle}
                  className="mr-2 h-4 w-4"
                />
                <label
                  htmlFor="hasCustomPrice"
                  className="text-xs text-gray-600"
                >
                  Utiliser un prix spécifique
                </label>
              </div>
            </div>
            <div className="flex items-center">
              <div className="relative flex-1">
                <DollarSign className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="number"
                  min="0"
                  disabled={!currentVariant.hasCustomPrice}
                  value={
                    currentVariant.hasCustomPrice
                      ? currentVariant.price
                      : defaultPrice || 0
                  }
                  onChange={(e) =>
                    setCurrentVariant((prev) => ({
                      ...prev,
                      price: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full pl-8 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              <span className="ml-2 text-sm text-gray-500">€</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Prix promo
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isOnPromo"
                  checked={currentVariant.isOnPromo}
                  onChange={handlePromoToggle}
                  className="mr-2 h-4 w-4"
                />
                <label htmlFor="isOnPromo" className="text-xs text-gray-600">
                  En promotion
                </label>
              </div>
            </div>
            <div className="flex items-center">
              <div className="relative flex-1">
                <Tag className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="number"
                  min="0"
                  disabled={!currentVariant.isOnPromo}
                  value={
                    currentVariant.isOnPromo ? currentVariant.promoPrice : 0
                  }
                  onChange={(e) =>
                    setCurrentVariant((prev) => ({
                      ...prev,
                      promoPrice: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full pl-8 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
              <span className="ml-2 text-sm text-gray-500">€</span>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1 sm:mb-2">
            Type de tailles
          </label>
          <select
            value={sizeType}
            onChange={(e) => {
              setSizeType(e.target.value);
              setCurrentVariant((prev) => ({ ...prev, sizes: [] }));
            }}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 mb-3 sm:mb-4 text-sm sm:text-base"
          >
            <option value="clothing">Vêtements (XS-3XL)</option>
            <option value="shoes">Chaussures (36-46)</option>
            <option value="numeric">Tailles numériques (30-50)</option>
            <option value="oneSize">Taille unique</option>
            <option value="custom">Tailles personnalisées</option>
          </select>

          <div className="overflow-x-auto pb-2">
            <SizeSelector
              selectedSizes={currentVariant.sizes}
              onSizeToggle={handleSizeToggle}
              sizeType={sizeType}
            />
          </div>
        </div>

        <div className="flex space-x-2">
          <button
            style={{ cursor: "pointer" }}
            onClick={handleAddVariant}
            disabled={
              !currentVariant.colorName || currentVariant.sizes.length === 0
            }
            className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {editingVariantId ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Mettre à jour
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter la variante
              </>
            )}
          </button>

          {editingVariantId && (
            <button
              onClick={cancelEdit}
              className="flex items-center justify-center px-3 py-2 bg-gray-500 text-white text-sm sm:text-base rounded-md hover:bg-gray-600 transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </button>
          )}
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 sm:mb-4">
          Variantes ajoutées ({variants.length})
        </h3>

        <div className="grid grid-cols-1 gap-3 sm:gap-4">
          {variants.map((variant) => (
            <div
              key={variant.id}
              className={`p-3 sm:p-4 border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow ${
                editingVariantId === variant.id
                  ? "border-blue-500 ring-2 ring-blue-200"
                  : ""
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start sm:items-center space-x-2 sm:space-x-3">
                  {variant.imagePreview || variant.imageUrl ? (
                    <img
                      src={variant.imagePreview || variant.imageUrl}
                      alt={variant.colorName}
                      className="h-12 w-12 sm:h-16 sm:w-16 rounded-md object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-md bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <ImageIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-4 h-4 sm:w-6 sm:h-6 rounded-full border flex-shrink-0"
                        style={{
                          backgroundColor: variant.color || variant.colorCode,
                        }}
                      />
                      <span className="font-medium text-sm sm:text-base truncate">
                        {variant.colorName}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {variant.sizes.map((size) => (
                        <span
                          key={size}
                          className="px-1 sm:px-2 py-0.5 sm:py-1 text-xs bg-gray-100 rounded"
                        >
                          {size}
                        </span>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                      <span>Stock: {variant.stock}</span>
                      {variant.hasCustomPrice && (
                        <span>Prix: {variant.price} €</span>
                      )}
                      {variant.isOnPromo && (
                        <span className="text-green-600">
                          Promo: {variant.promoPrice} €
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-1">
                  {isEditMode && (
                    <button
                      onClick={() => handleEditVariant(variant)}
                      disabled={editingVariantId === variant.id}
                      className="text-blue-500 hover:text-blue-700 p-1 flex-shrink-0"
                    >
                      <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  )}
                  <button
                    onClick={() =>
                      handleRemoveVariant(
                        variant.id,
                        variant?._id ? variant._id : null
                      )
                    }
                    className="text-red-500 hover:text-red-700 p-1 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
